import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { Order, Rating, RatingReasons } from 'models/order';
import { Alert, Box, Checkbox, FormControl, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { approveOrderTemplate, getOrderDetailsTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import PriceSpecificationSectionWithoutHighlighting from 'components/order-details/main-order-view/price-specification-section/price-specification-section-without-highlighting.componet';
import RateOrderContent from 'components/orders/order-details/rate-order/rate-order-content.component';
import { LoadingButton } from '@mui/lab';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type ApprovedOrderDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    orderId?: string;
};

const ApprovedOrderDialog: FC<ApprovedOrderDialogProps> = ({
    onUpdate,
    onClose,
    open,
    orderId,
}: ApprovedOrderDialogProps) => {
    const { t: translate } = useTranslation(['orders']);
    const currentOrganization = currentOrgVar();
    const isCertificationEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CERTIFICATION,
    );
    const [isPriceConfirmed, setIsPriceConfirmed] = useState(false);
    const [isArchive, setIsArchive] = useState(true);
    const [rating, setRating] = useState<Rating>({} as Rating);
    const [isLoading, setLoading] = useState(false);
    const [order, setOrder] = useState<Order>();
    const { showBoundary } = useDenthubErrorBoundary();

    const [getOrder, { loading }] = useLazyQuery(getOrderDetailsTemplate, {
        onCompleted({ order }) {
            setOrder(order);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        getOrder({
            variables: {
                orderId: orderId,
            },
        });
    }, [orderId]);

    useEffect(() => {
        if (order) {
            setRating(order.rating);
        }
    }, [order]);

    const handleRating = (rating: Rating) => {
        setRating(rating);
    };

    const disableRateButton = () => {
        if (rating.satisfied) {
            return false;
        } else if (rating.notSatisfied) {
            return rating.reasons?.every((reason: RatingReasons) => !reason.value);
        }
        return true;
    };

    const [approveOrder] = useMutation(approveOrderTemplate, {
        onCompleted: ({ approveOrder }) => {
            onClose();
            onUpdate?.(approveOrder);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleApproveOrder = async () => {
        setLoading(true);
        const ratingObj = {
            satisfied: rating.satisfied,
            notSatisfied: rating.notSatisfied,
            reasons: rating.reasons,
            comments: rating.comments,
        };
        await approveOrder({
            variables: {
                orderId: order?._id,
                isPriceConfirmed: isPriceConfirmed,
                rating: ratingObj,
                isArchive: isArchive,
            },
        });
        setLoading(false);
    };

    const handleConfirmPrice = (event: ChangeEvent<HTMLInputElement>) => {
        setIsPriceConfirmed(event.target.checked);
    };

    const handleArchive = (event: ChangeEvent<HTMLInputElement>) => {
        setIsArchive(event.target.checked);
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={onClose}
            title={translate('orders:APPROVE_THE_ORDER')}
            actions={
                <>
                    <FormControl margin="dense" fullWidth>
                        <FormControlLabel
                            control={<Checkbox color="secondary" checked={isArchive} onChange={handleArchive} />}
                            label={translate('orders:ARCHIVE_THE_ORDER')}
                            data-test="approve-order-dialog.archive-order-checkbox"
                        />
                    </FormControl>
                    <LoadingButton
                        disabled={!isPriceConfirmed || disableRateButton()}
                        sx={{ minWidth: 250 }}
                        variant="contained"
                        onClick={handleApproveOrder}
                        loading={isLoading}
                        data-test="approve-order-dialog.change-status-button"
                    >
                        {translate('orders:CHANGE_THE_STATUS')}
                    </LoadingButton>
                </>
            }
        >
            {order ? (
                <>
                    <Box mb={3}>
                        <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
                            <strong>{translate('orders:APPROVE_WARNING_MESSAGE')}</strong>
                        </Alert>
                        <PriceSpecificationSectionWithoutHighlighting order={order} currency={order.currency} />
                        <FormControl margin="dense" fullWidth>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={isPriceConfirmed}
                                        onChange={handleConfirmPrice}
                                        data-test="approve-order-dialog.confirm-price-checkbox"
                                        size="large"
                                    />
                                }
                                label={
                                    isCertificationEnabled
                                        ? translate('orders:CONFIRM_CERTIFY_PRICE')
                                        : translate('orders:CONFIRM_PRICE')
                                }
                            />
                        </FormControl>
                    </Box>
                    {!order.isRated ? (
                        <Box mb={3}>
                            <RateOrderContent onHandleRating={handleRating} order={order} />
                        </Box>
                    ) : undefined}
                </>
            ) : undefined}
        </DHDialog>
    );
};
export default ApprovedOrderDialog;
